import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { memo } from "react";
import { AccordionContent } from "./AccordionContent";
import { AccordionHeader } from "./AccordionHeader";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface SellerCartAccordionProps {
  /**
   * The seller cart data
   */
  cart: StoreSellerCart;
  /**
   * Whether this accordion is currently expanded
   */
  isExpanded: boolean;
  /**
   * Callback when accordion expand/collapse is toggled
   */
  onToggle: () => void;
  /**
   * Display variant - affects styling
   * - "by-supplier": Individual cart accordion in Group by Supplier tab
   * - "all-items": Not used anymore (All Items tab shows flat list)
   */
  variant?: "by-supplier" | "all-items";
  /** Currently applied promo code for this cart, if any. */
  appliedPromoCode?: string;
  /** Promo code error message for this cart, if any. */
  promoError?: string;
  /** Apply a promo code to this cart (renders the footer promo input). */
  onApplyPromo?: (cartId: string, code: string) => Promise<void>;
  /** Remove this cart's applied promo code. */
  onRemovePromo?: (cartId: string) => Promise<void>;
  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Seller cart card.
 *
 * A supplier's cart rendered as a card in checkout's `rounded-xl` section-card
 * idiom (KAN-69), so walking from cart to checkout reads as one continuous
 * flow. Collapsed by default - the header carries the supplier's total and
 * checkout action - and independently expandable (the parent owns that state).
 * - Header shows seller info, item count, total, and per-seller checkout
 * - Content shows line items, shipping selector, and totals breakdown
 *
 * @example
 * ```tsx
 * // Group by Supplier tab (collapsed by default, independently expandable)
 * <SellerCartAccordion
 *   cart={cart}
 *   isExpanded={expandedCartIds.has(cart.cart_id)}
 *   onToggle={() => onToggleAccordion(cart.cart_id)}
 *   variant="by-supplier"
 * />
 * ```
 */
function SellerCartAccordionComponent({
  cart,
  isExpanded,
  onToggle,
  variant = "all-items",
  appliedPromoCode,
  promoError,
  onApplyPromo,
  onRemovePromo,
  className
}: SellerCartAccordionProps) {
  const contentId = `cart-content-${cart.cart_id}`;
  const headerId = `cart-header-${cart.cart_id}`;

  return (
    // One container owns the border and radius so the header reads as the top
    // of this supplier's box rather than a sealed card sitting above a
    // separate slab. `overflow-hidden` clips the body's square bottom corners
    // to the radius, so the body needs no rounding of its own.
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-gray-200 bg-white",
        className
      )}
    >
      <AccordionHeader
        cart={cart}
        isExpanded={isExpanded}
        onToggle={onToggle}
        variant={variant}
        headerId={headerId}
        contentId={contentId}
      />

      {/* Always mounted, just hidden - conditionally unmounting this would
          tear down any in-progress interaction inside it (e.g. an open
          remove-confirmation dialog) whenever `isExpanded` flips, including
          for reasons unrelated to the user collapsing it themselves. */}
      <AccordionContent
        cart={cart}
        contentId={contentId}
        headerId={headerId}
        hidden={!isExpanded}
        appliedPromoCode={appliedPromoCode}
        promoError={promoError}
        onApplyPromo={onApplyPromo}
        onRemovePromo={onRemovePromo}
      />
    </div>
  );
}

export const SellerCartAccordion = memo(SellerCartAccordionComponent);
